import { CalendarDays, ClipboardList, Search, User, Wallet } from "lucide-react";
import { Navigate, Route, Routes } from "react-router-dom";

import { MyApplications } from "@/features/applications";
import { useCurrentUser, useLogout } from "@/features/auth";
import { AvailabilityPage } from "@/features/availability";
import { EarningsView } from "@/features/earnings";
import { ShiftDetail, ShiftFeed } from "@/features/shifts";
import { PendingReviewView, useWorkerProfile, WorkerProfileView } from "@/features/workers";
import { AppShell } from "@/shared/ui";

export function WorkerHomePage() {
  const { data: user } = useCurrentUser();
  const { data: profile, isLoading: profileLoading } = useWorkerProfile();
  const logout = useLogout();

  // Force new workers through onboarding before they can browse shifts.
  // Avoid a flicker by waiting on the profile fetch.
  if (profileLoading) {
    return null;
  }
  if (profile && !profile.onboarding_completed_at) {
    return <Navigate to="/worker/onboarding" replace />;
  }

  // Until ops approves the profile, the apply endpoint will reject the worker
  // and the shift feed isn't useful. Replace shift-/earnings-/applications-
  // related tabs with a friendly review-status card. Profile stays editable
  // so the worker can fix anything ops flags.
  const gated =
    profile && profile.status !== "APPROVED" ? (
      <PendingReviewView status={profile.status} />
    ) : null;

  return (
    <AppShell
      roleLabel="Worker"
      userEmail={user?.email}
      onLogout={logout}
      tabs={[
        { to: "/worker", label: "Shifts", icon: Search },
        { to: "/worker/availability", label: "Availability", icon: CalendarDays },
        { to: "/worker/applications", label: "Applications", icon: ClipboardList },
        { to: "/worker/earnings", label: "Earnings", icon: Wallet },
        { to: "/worker/profile", label: "Profile", icon: User },
      ]}
    >
      <Routes>
        <Route index element={gated ?? <ShiftFeed />} />
        <Route path="availability" element={<AvailabilityPage />} />
        <Route path="shifts/:id" element={gated ?? <ShiftDetail />} />
        <Route path="applications" element={gated ?? <MyApplications />} />
        <Route path="earnings" element={gated ?? <EarningsView />} />
        <Route path="profile" element={<WorkerProfileView />} />
        <Route path="*" element={<Navigate to="/worker" replace />} />
      </Routes>
    </AppShell>
  );
}
